/**
 * RateCurrent wordmark — canonical brand mark, single source of truth for every
 * surface that renders the logo (Header.jsx, Footer.jsx, and any future surface)
 * so the mark can never drift between them.
 *
 * Martha-BRM, 2026-09-28: replaces the stock lucide-react "Zap" bolt glyph inside
 * a flat #16A34A rounded square, which the Owner flagged as reading cartoonish —
 * correctly, since it was never a designed mark, just a default icon-library glyph
 * in a colored box (the visual language of a placeholder app icon, not a brand).
 * Full rationale: `AIOpCo Docs & Templates/Logo Redesign Rationale.md`.
 *
 * Direction: icon-free, two-tone wordmark. "Rate" renders in ink navy, "Current"
 * in brand green — literally the word that carries the energy/electricity meaning,
 * so the color does the work an icon would have, without an extra decorative glyph
 * that added no information (the old mark was flagged decorative-only in
 * Design Specification.md Section 3.5). This is the same category of solution many
 * serious fintech/comparison brands use (Wealthfront, Chime, Ramp) — strong
 * typography as the mark, not a bolted-on icon.
 *
 * Color rule: "Current" uses --rc-green-700 (#15803D), never --rc-green-600/
 * #16A34A. Design Specification.md Section 3.1/F6 already ratified that the raw
 * #16A34A fails contrast for text and restricted it to "logo fill and decorative
 * graphics only" — #15803D is the token already approved for "emphasis text on
 * white." Using it here keeps the wordmark itself accessible at any size, with no
 * separate legibility carve-out needed.
 *
 * A square, icon-only version of this same identity (for the favicon and any
 * future square-only slot — social avatars, app icons) lives separately as a
 * navy-and-white "R" monogram at `frontend/public/favicon.svg`, not as a
 * duplicate of this wordmark. Two different lockups for two different jobs,
 * not one mark trying to do both.
 */
/**
 * variant="dark": for placement on a navy/dark background (e.g. the footer) --
 * "Rate" switches from ink navy to white so it stays legible against #0A2540,
 * the same literal color this wordmark sits on there. "Current" keeps its own
 * green either way; it was already chosen for contrast on white and reads fine
 * on navy too.
 */
export default function BrandMark({ className = "", variant = "light" }) {
  return (
    <span className={`font-display tracking-tight font-extrabold whitespace-nowrap ${className}`}>
      <span className={variant === "dark" ? "text-white" : "text-[#0A2540]"}>Rate</span>
      <span className="text-[#15803D]">Current</span>
    </span>
  );
}
